<template>
  <div class="pensamiento-editor-wrap">
    <div class="pensamiento-toolbar" role="toolbar" aria-label="Formato de la reflexión">
      <button type="button" class="tb" :class="{ on: isActive('bold') }" title="Negrita" @click="run('toggleBold')">
        <i class="bi bi-type-bold"></i>
      </button>
      <button type="button" class="tb" :class="{ on: isActive('italic') }" title="Cursiva" @click="run('toggleItalic')">
        <i class="bi bi-type-italic"></i>
      </button>
      <button type="button" class="tb" :class="{ on: isActive('strike') }" title="Tachado" @click="run('toggleStrike')">
        <i class="bi bi-type-strikethrough"></i>
      </button>
      <span class="tb-sep"></span>
      <button type="button" class="tb" :class="{ on: isActive('heading', { level: 2 }) }" title="Título" @click="toggleHeading(2)">
        H2
      </button>
      <button type="button" class="tb" :class="{ on: isActive('heading', { level: 3 }) }" title="Subtítulo" @click="toggleHeading(3)">
        H3
      </button>
      <span class="tb-sep"></span>
      <button type="button" class="tb" :class="{ on: isActive('bulletList') }" title="Lista" @click="run('toggleBulletList')">
        <i class="bi bi-list-ul"></i>
      </button>
      <button type="button" class="tb" :class="{ on: isActive('orderedList') }" title="Lista numerada" @click="run('toggleOrderedList')">
        <i class="bi bi-list-ol"></i>
      </button>
      <button type="button" class="tb" :class="{ on: isActive('blockquote') }" title="Cita" @click="run('toggleBlockquote')">
        <i class="bi bi-quote"></i>
      </button>
      <span class="tb-sep"></span>
      <button type="button" class="tb" title="Enlace" @click="setLink">
        <i class="bi bi-link-45deg"></i>
      </button>
      <button type="button" class="tb" title="Añadir fotos" :disabled="ocupado" @click="elegirImagen">
        <span v-if="subiendo" class="spinner-border spinner-border-sm"></span>
        <i v-else class="bi bi-images"></i>
      </button>
      <button
        type="button"
        class="tb"
        :class="{ rec: grabando }"
        :title="grabando ? 'Detener y transcribir' : 'Grabar audio y transcribir'"
        :disabled="transcribiendo"
        @click="toggleGrabacion"
      >
        <span v-if="transcribiendo" class="spinner-border spinner-border-sm"></span>
        <i v-else class="bi" :class="grabando ? 'bi-stop-circle-fill' : 'bi-mic'"></i>
      </button>
      <span v-if="grabando" class="rec-timer" aria-live="polite">{{ timerLabel }}</span>
      <input
        ref="fileInput"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif"
        multiple
        class="d-none"
        @change="onFile"
      />
      <input
        ref="audioInput"
        type="file"
        accept="audio/webm,audio/ogg,audio/mpeg,audio/mp4,audio/wav,.webm,.ogg,.mp3,.m4a,.wav"
        class="d-none"
        @change="onAudioFile"
      />
    </div>

    <div
      class="fotos-panel"
      :class="{ on: arrastrando }"
      @dragenter.prevent="arrastrando = true"
      @dragover.prevent="arrastrando = true"
      @dragleave.prevent="arrastrando = false"
      @drop.prevent="onDropZone"
    >
      <div class="fotos-actions">
        <button type="button" class="btn btn-sm btn-warning" :disabled="ocupado" @click="elegirImagen">
          <i class="bi bi-images me-1"></i>
          Elegir fotos
        </button>
        <button type="button" class="btn btn-sm btn-outline-warning" :disabled="ocupado" @click="elegirAudio">
          <i class="bi bi-mic me-1"></i>
          Subir audio
        </button>
        <form class="fotos-enlace" @submit.prevent="importarEnlace">
          <label class="visually-hidden" for="enlace-google-fotos">Enlace de Google Fotos</label>
          <input
            id="enlace-google-fotos"
            v-model="enlaceFotos"
            type="url"
            class="form-control form-control-sm"
            placeholder="Pega un enlace de Google Fotos"
            :disabled="ocupado"
          />
          <button type="submit" class="btn btn-sm btn-outline-secondary" :disabled="ocupado || !enlaceFotos.trim()">
            Importar
          </button>
        </form>
      </div>
      <p class="fotos-ayuda mb-0">
        Puedes seleccionar <strong>varias a la vez</strong>, arrastrarlas o pegarlas.
        Desde Google Fotos: Comparte con <em>Vida</em>, elige varias en el selector, o pega un enlace de álbum.
        También puedes pulsar el micrófono para dictar la reflexión: se guarda el audio y se transcribe al texto.
      </p>
    </div>
    <p v-if="grabando" class="rec-banner mb-0">
      <i class="bi bi-record-fill me-1"></i>
      Grabando {{ timerLabel }} — pulsa el micrófono otra vez para transcribir.
    </p>

    <editor-content :editor="editor" class="pensamiento-surface" />
    <p v-if="progreso" class="small text-muted aviso-editor">
      <span class="spinner-border spinner-border-sm me-1"></span>
      Importando {{ progreso.hechas }}/{{ progreso.total }}…
    </p>
    <p v-if="transcribiendo" class="small text-muted aviso-editor">
      <span class="spinner-border spinner-border-sm me-1"></span>
      Transcribiendo el audio…
    </p>
    <p v-if="avisoImagen" class="small text-danger aviso-editor">{{ avisoImagen }}</p>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import axios from '../config/axios';
import { htmlToMarkdown, markdownToHtml } from '../utils/pensamientosRich';
import {
  MAX_BYTES,
  collectClipboardPayload,
  filesFromDrop,
  filesFromList,
  heicWarning,
  isHeicFile,
  mimeForFile,
} from '../utils/pensamientosFotos';
import {
  MAX_AUDIO_BYTES,
  MAX_AUDIO_MS,
  formatRecTimer,
  isAcceptedAudioFile,
  mimeForAudioFile,
  pickRecorderMime,
  transcriptToHtml,
} from '../utils/pensamientosAudio';
import { takeSharedPhotos } from '../utils/pensamientosShareTarget';
import PensamientoAudio from '../extensions/PensamientoAudio';

function apiBase() {
  return axios.defaults.baseURL || '';
}

function toEditorSrc(src) {
  const raw = String(src || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${apiBase()}${raw}`;
}

function toStoredSrc(src) {
  return String(src || '')
    .replace(/^https?:\/\/localhost:\d+/i, '')
    .replace(/^https?:\/\/gastos\.vicenteserran\.com/i, '');
}

function rewriteMediaSrc(html, mapper) {
  return String(html || '')
    .replace(/<(img|audio)\b([^>]*?)\bsrc="([^"]+)"([^>]*)/gi, (_, tag, before, src, after) => (
      `<${tag}${before}src="${mapper(src)}"${after}`
    ));
}

function markdownForEditor(md) {
  return rewriteMediaSrc(markdownToHtml(md || ''), toEditorSrc);
}

function markdownFromEditor(html) {
  return htmlToMarkdown(rewriteMediaSrc(html, toStoredSrc));
}

export default {
  name: 'PensamientoEditor',
  components: { EditorContent },
  props: {
    modelValue: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const editor = ref(null);
    const fileInput = ref(null);
    const audioInput = ref(null);
    const subiendo = ref(false);
    const transcribiendo = ref(false);
    const grabando = ref(false);
    const recMs = ref(0);
    const avisoImagen = ref('');
    const enlaceFotos = ref('');
    const arrastrando = ref(false);
    const progreso = ref(null);
    const activeTick = ref(0);
    const ocupado = computed(() => subiendo.value || transcribiendo.value || grabando.value);
    const timerLabel = computed(() => formatRecTimer(recMs.value));
    let applying = false;
    let mediaRecorder = null;
    let recChunks = [];
    let recStream = null;
    let recTick = null;
    let recStartedAt = 0;
    let recStopping = false;

    function isActive(name, opts) {
      activeTick.value;
      return editor.value?.isActive(name, opts) || false;
    }

    function emitMarkdown() {
      if (!editor.value || applying) return;
      emit('update:modelValue', markdownFromEditor(editor.value.getHTML()));
    }

    function run(command) {
      editor.value?.chain().focus()[command]().run();
    }

    function toggleHeading(level) {
      editor.value?.chain().focus().toggleHeading({ level }).run();
    }

    function setLink() {
      if (!editor.value) return;
      const prev = editor.value.getAttributes('link').href || '';
      const url = window.prompt('URL del enlace', prev);
      if (url === null) return;
      if (!url.trim()) {
        editor.value.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      editor.value.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
    }

    function insertarImagen(url, alt) {
      const src = toEditorSrc(url);
      if (!src || !editor.value) return;
      editor.value.chain().focus().setImage({ src, alt: alt || '' }).run();
    }

    function insertarAudioYTexto(url, text) {
      if (!editor.value) return;
      const src = toEditorSrc(url);
      if (src) {
        editor.value.chain().focus().setAudio({ src, title: 'Nota de voz' }).run();
      }
      const html = transcriptToHtml(text);
      if (html) {
        editor.value.chain().focus().insertContent(html).run();
      }
    }

    function elegirImagen() {
      avisoImagen.value = '';
      fileInput.value?.click();
    }

    function elegirAudio() {
      avisoImagen.value = '';
      audioInput.value?.click();
    }

    function limpiarGrabacion() {
      if (recTick) {
        clearInterval(recTick);
        recTick = null;
      }
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        try { mediaRecorder.stop(); } catch { /* ignore */ }
      }
      mediaRecorder = null;
      recChunks = [];
      recStopping = false;
      if (recStream) {
        recStream.getTracks().forEach((track) => track.stop());
        recStream = null;
      }
      grabando.value = false;
      recMs.value = 0;
    }

    async function subirAudioBlob(blob, mime) {
      if (!blob || blob.size < 32) {
        throw new Error('La grabación está vacía. Prueba otra vez.');
      }
      if (blob.size > MAX_AUDIO_BYTES) {
        throw new Error('El audio supera 12 MB. Graba un poco más corto.');
      }
      transcribiendo.value = true;
      avisoImagen.value = '';
      try {
        const { data } = await axios.post('/api/pensamientos/media/audio', blob, {
          headers: { 'Content-Type': mime || blob.type || 'audio/webm' },
          transformRequest: [(body) => body],
          timeout: 90000,
        });
        insertarAudioYTexto(data.url, data.text);
        if (data.warning) avisoImagen.value = data.warning;
        else if (!data.text) avisoImagen.value = 'Audio guardado, pero no se pudo transcribir.';
      } finally {
        transcribiendo.value = false;
      }
    }

    async function empezarGrabacion() {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
        avisoImagen.value = 'Este navegador no puede grabar audio. Sube un archivo o usa Chrome/Safari recientes.';
        return;
      }
      avisoImagen.value = '';
      try {
        recStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        avisoImagen.value = 'No hay permiso de micrófono. Puedes subirlo como archivo.';
        return;
      }
      const mime = pickRecorderMime();
      recChunks = [];
      recStopping = false;
      try {
        mediaRecorder = mime ? new MediaRecorder(recStream, { mimeType: mime }) : new MediaRecorder(recStream);
      } catch {
        recStream.getTracks().forEach((track) => track.stop());
        recStream = null;
        avisoImagen.value = 'No se pudo iniciar la grabación en este dispositivo.';
        return;
      }
      mediaRecorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size) recChunks.push(ev.data);
      };
      mediaRecorder.onerror = () => {
        avisoImagen.value = 'Error al grabar el audio.';
        limpiarGrabacion();
      };
      recStartedAt = Date.now();
      recMs.value = 0;
      grabando.value = true;
      recTick = setInterval(() => {
        recMs.value = Date.now() - recStartedAt;
        if (!recStopping && recMs.value >= MAX_AUDIO_MS && mediaRecorder && mediaRecorder.state === 'recording') {
          detenerGrabacion();
        }
      }, 250);
      mediaRecorder.start(250);
    }

    function detenerGrabacion() {
      return new Promise((resolve) => {
        const recorder = mediaRecorder;
        if (recStopping) {
          resolve();
          return;
        }
        if (!recorder || recorder.state === 'inactive') {
          limpiarGrabacion();
          resolve();
          return;
        }
        recStopping = true;
        recorder.onstop = async () => {
          const type = recorder.mimeType || pickRecorderMime() || 'audio/webm';
          const blob = new Blob(recChunks, { type: type.split(';')[0] });
          limpiarGrabacion();
          try {
            await subirAudioBlob(blob, type.split(';')[0]);
          } catch (e) {
            avisoImagen.value = e.response?.data?.error || e.message || 'No se pudo transcribir el audio';
          }
          resolve();
        };
        recorder.stop();
      });
    }

    async function toggleGrabacion() {
      if (transcribiendo.value) return;
      if (grabando.value) {
        await detenerGrabacion();
        return;
      }
      await empezarGrabacion();
    }

    async function onAudioFile(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (audioInput.value) audioInput.value.value = '';
      if (!file) return;
      if (!isAcceptedAudioFile(file)) {
        avisoImagen.value = 'Usa un audio WEBM, OGG, MP3, M4A o WAV.';
        return;
      }
      try {
        await subirAudioBlob(file, mimeForAudioFile(file));
      } catch (e) {
        avisoImagen.value = e.response?.data?.error || e.message || 'No se pudo transcribir el audio';
      }
    }

    async function subirUna(file) {
      if (file.size > MAX_BYTES) {
        throw new Error(`«${file.name || 'foto'}» supera 12 MB`);
      }
      const { data } = await axios.post('/api/pensamientos/media', file, {
        headers: { 'Content-Type': mimeForFile(file) },
        transformRequest: [(body) => body],
        timeout: 60000,
      });
      insertarImagen(data.url, file.name || '');
    }

    async function importarArchivos(list) {
      const all = filesFromList(list);
      if (!all.length) {
        avisoImagen.value = 'Usa fotos JPG, PNG, WEBP o GIF. Puedes elegir varias a la vez.';
        return;
      }
      const heic = all.filter(isHeicFile);
      const ok = all.filter((file) => !isHeicFile(file));
      const avisos = [];
      if (heic.length) avisos.push(heicWarning(heic.length));
      if (!ok.length) {
        avisoImagen.value = avisos.join(' ');
        return;
      }
      subiendo.value = true;
      avisoImagen.value = '';
      progreso.value = { hechas: 0, total: ok.length };
      const fallidas = [];
      try {
        for (const file of ok) {
          try {
            await subirUna(file);
          } catch (e) {
            fallidas.push(e.response?.data?.error || e.message || file.name);
          }
          progreso.value = { hechas: progreso.value.hechas + 1, total: ok.length };
        }
        if (fallidas.length) {
          avisos.push(
            fallidas.length === ok.length
              ? fallidas[0]
              : `${ok.length - fallidas.length} fotos añadidas. No se pudieron subir ${fallidas.length}.`
          );
        }
        avisoImagen.value = avisos.join(' ');
      } finally {
        subiendo.value = false;
        progreso.value = null;
        if (fileInput.value) fileInput.value.value = '';
      }
    }

    async function importarFuentes(sources) {
      const list = [...new Set((sources || []).map((item) => String(item || '').trim()).filter(Boolean))];
      if (!list.length) return;
      subiendo.value = true;
      avisoImagen.value = '';
      progreso.value = { hechas: 0, total: list.length };
      try {
        const { data } = await axios.post(
          '/api/pensamientos/media/import',
          list.length === 1 ? { source: list[0] } : { urls: list },
          { timeout: 180000 }
        );
        const imported = data.imported || [];
        imported.forEach((item) => insertarImagen(item.url, item.filename || ''));
        progreso.value = { hechas: imported.length, total: Math.max(imported.length, list.length) };
        if (data.failed?.length) {
          avisoImagen.value = imported.length
            ? `Se importaron ${imported.length}. ${data.failed.length} no se pudieron bajar.`
            : (data.failed[0].error || 'No se pudieron importar las fotos');
        } else if (imported.length) {
          enlaceFotos.value = '';
        }
      } catch (e) {
        avisoImagen.value = e.response?.data?.error || 'No se pudieron importar las fotos';
      } finally {
        subiendo.value = false;
        progreso.value = null;
      }
    }

    function onFile(ev) {
      importarArchivos(ev.target.files);
    }

    async function importarEnlace() {
      const source = enlaceFotos.value.trim();
      if (!source) return;
      await importarFuentes([source]);
    }

    function onDropZone(event) {
      arrastrando.value = false;
      const payload = filesFromDrop(event.dataTransfer);
      if (payload.files.length) importarArchivos(payload.files);
      if (payload.urls.length) importarFuentes(payload.urls);
    }

    onMounted(() => {
      editor.value = new Editor({
        extensions: [
          StarterKit.configure({
            heading: { levels: [2, 3] },
            codeBlock: false,
          }),
          Image.configure({
            inline: false,
            allowBase64: false,
            HTMLAttributes: { class: 'pensamiento-img' },
          }),
          PensamientoAudio,
          Link.configure({
            openOnClick: false,
            autolink: true,
            HTMLAttributes: { rel: 'noopener', target: '_blank' },
          }),
          Placeholder.configure({
            placeholder: 'Escribe cómo ha ido el día, qué has aprendido, qué quieres recordar…',
          }),
        ],
        content: markdownForEditor(props.modelValue),
        editorProps: {
          attributes: {
            class: 'pensamiento-prose',
          },
          handlePaste(_view, event) {
            const payload = collectClipboardPayload(event.clipboardData);
            if (!payload.files.length && !payload.urls.length) return false;
            event.preventDefault();
            if (payload.files.length) importarArchivos(payload.files);
            if (payload.urls.length) importarFuentes(payload.urls);
            return true;
          },
          handleDrop(_view, event) {
            const payload = filesFromDrop(event.dataTransfer);
            if (!payload.files.length && !payload.urls.length) return false;
            event.preventDefault();
            arrastrando.value = false;
            if (payload.files.length) importarArchivos(payload.files);
            if (payload.urls.length) importarFuentes(payload.urls);
            return true;
          },
        },
        onUpdate: () => {
          emitMarkdown();
        },
        onSelectionUpdate: () => {
          activeTick.value += 1;
        },
        onTransaction: () => {
          activeTick.value += 1;
        },
      });

      takeSharedPhotos().then(({ files, urls }) => {
        if (files.length) importarArchivos(files);
        if (urls.length) importarFuentes(urls);
      });
    });

    watch(() => props.modelValue, (val) => {
      if (!editor.value) return;
      const incoming = markdownFromEditor(markdownForEditor(val || ''));
      const current = markdownFromEditor(editor.value.getHTML());
      if (incoming === current) return;
      applying = true;
      editor.value.commands.setContent(markdownForEditor(val || ''), false);
      applying = false;
    });

    onBeforeUnmount(() => {
      limpiarGrabacion();
      editor.value?.destroy();
      editor.value = null;
    });

    return {
      editor,
      fileInput,
      audioInput,
      subiendo,
      transcribiendo,
      grabando,
      ocupado,
      timerLabel,
      avisoImagen,
      enlaceFotos,
      arrastrando,
      progreso,
      isActive,
      run,
      toggleHeading,
      setLink,
      elegirImagen,
      elegirAudio,
      toggleGrabacion,
      onFile,
      onAudioFile,
      importarEnlace,
      onDropZone,
    };
  },
};
</script>

<style scoped>
.pensamiento-editor-wrap {
  border: 1px solid #e4d2a8;
  border-radius: 12px;
  background: #fffdf6;
  overflow: hidden;
}
.pensamiento-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 8px 10px;
  background: linear-gradient(180deg, #fff8e6, #f7ecd2);
  border-bottom: 1px solid #ead9b0;
}
.tb {
  border: 0;
  background: transparent;
  color: #5c4a24;
  width: 34px;
  height: 32px;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1;
}
.tb:hover,
.tb.on {
  background: #fff;
  color: #3d2e12;
  box-shadow: inset 0 0 0 1px #e0c98a;
}
.tb:disabled {
  opacity: 0.55;
}
.tb.rec {
  color: #b42318;
  background: #ffe8e5;
  box-shadow: inset 0 0 0 1px #f0b0a8;
}
.rec-timer {
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
  color: #b42318;
  align-self: center;
  margin-left: 4px;
  font-weight: 700;
}
.rec-banner {
  padding: 8px 12px;
  background: #ffe8e5;
  color: #8a2218;
  font-size: 0.82rem;
  border-bottom: 1px solid #f0b0a8;
}
.tb-sep {
  width: 1px;
  background: #e0c98a;
  margin: 4px 6px;
}
.fotos-panel {
  padding: 10px 12px 12px;
  background: #fffaf0;
  border-bottom: 1px dashed #ead9b0;
}
.fotos-panel.on {
  background: #fff3cc;
  box-shadow: inset 0 0 0 2px #e0c98a;
}
.fotos-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.fotos-enlace {
  display: flex;
  flex: 1 1 220px;
  gap: 6px;
  min-width: 0;
}
.fotos-ayuda {
  margin-top: 8px;
  font-size: 0.78rem;
  color: #7a6840;
  line-height: 1.45;
}
.pensamiento-surface {
  min-height: 320px;
}
.pensamiento-surface :deep(.ProseMirror) {
  min-height: 320px;
  padding: 16px 18px 28px;
  outline: none;
  font-size: 1.05rem;
  line-height: 1.7;
  color: #2b2416;
}
.pensamiento-surface :deep(.ProseMirror p) {
  margin-bottom: 0.75rem;
}
.pensamiento-surface :deep(.ProseMirror h2) {
  font-size: 1.35rem;
  margin: 1rem 0 0.5rem;
}
.pensamiento-surface :deep(.ProseMirror h3) {
  font-size: 1.15rem;
  margin: 0.9rem 0 0.4rem;
}
.pensamiento-surface :deep(.ProseMirror blockquote) {
  border-left: 3px solid #e0c98a;
  margin: 0 0 0.75rem;
  padding: 0.15rem 0 0.15rem 0.9rem;
  color: #5c4a24;
}
.pensamiento-surface :deep(.pensamiento-img),
.pensamiento-surface :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  margin: 0.4rem 0 1rem;
  display: block;
}
.pensamiento-surface :deep(audio),
.pensamiento-surface :deep(.pensamiento-audio) {
  width: 100%;
  max-width: 420px;
  margin: 0.4rem 0 1rem;
  display: block;
}
.pensamiento-surface :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  color: #b09a6a;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}
.aviso-editor {
  margin: 0;
  padding: 8px 14px 12px;
}
</style>
